import { useEffect } from "react";
import { createRoot } from "react-dom/client";

import { FirebaseAuthProvider } from "@/components/auth/FirebaseAuthProvider";
import { useFirebaseAuth } from "@/components/auth/FirebaseAuthProvider";
import {
  TurkuDetailFavorite,
  type TurkuFavoriteLabels,
} from "@/components/turku/TurkuDetailFavorite";
import { normalizeTurkuSlugKey } from "@/lib/turkuSlug";
import {
  getSongIdForSlug,
  loadSongLookup,
} from "@/lib/turkuSongLookup";

function labelsFromElement(el: HTMLElement): TurkuFavoriteLabels | null {
  const signIn = el.dataset.labelSignIn;
  const signInAction = el.dataset.labelSignInAction;
  const dismiss = el.dataset.labelDismiss;
  const addToFavorites = el.dataset.labelAdd;
  const removeFromFavorites = el.dataset.labelRemove;
  const favoriteError = el.dataset.labelError;
  if (
    signIn &&
    addToFavorites &&
    removeFromFavorites &&
    favoriteError
  ) {
    return {
      signIn,
      signInAction: signInAction ?? signIn,
      dismiss: dismiss ?? "OK",
      addToFavorites,
      removeFromFavorites,
      favoriteError,
    };
  }
  return null;
}

function slugFromPage(): string | null {
  const match = window.location.pathname.match(/\/turku\/([^/]+)\/?$/);
  if (!match?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

async function resolveSongId(el: HTMLElement): Promise<string | null> {
  const fromAttr = el.dataset.songId?.trim();
  if (fromAttr) {
    return fromAttr;
  }

  const slugRaw = el.dataset.slug?.trim() || slugFromPage();
  if (!slugRaw) {
    return null;
  }

  try {
    const lookup = await loadSongLookup();
    const normalized = normalizeTurkuSlugKey(slugRaw);
    return (
      getSongIdForSlug(normalized, lookup) ??
      getSongIdForSlug(slugRaw, lookup)
    );
  } catch {
    return null;
  }
}

type FavoriteHydrationProps = {
  songId: string;
  labels: TurkuFavoriteLabels;
  accountHref: string;
  returnPath: string;
};

function FavoriteHydration({
  songId,
  labels,
  accountHref,
  returnPath,
}: FavoriteHydrationProps) {
  const { wakeAuth } = useFirebaseAuth();

  useEffect(() => {
    wakeAuth();
  }, [wakeAuth]);

  return (
    <TurkuDetailFavorite
      songId={songId}
      labels={labels}
      accountHref={accountHref}
      returnPath={returnPath}
    />
  );
}

async function mount(): Promise<void> {
  const el = document.getElementById("turku-favorite-mount");
  if (!el) {
    return;
  }

  const labels = labelsFromElement(el);
  const accountHref = el.dataset.accountHref?.trim() || "/tr/account/";
  const returnPath =
    el.dataset.returnPath?.trim() ||
    `${window.location.pathname}${window.location.search}`;

  const songId = await resolveSongId(el);

  if (!labels) {
    console.warn("[turku-favorite] missing labels on mount element");
    return;
  }

  if (!songId) {
    console.warn("[turku-favorite] could not resolve song id");
    return;
  }

  try {
    createRoot(el).render(
      <FirebaseAuthProvider>
        <FavoriteHydration
          songId={songId}
          labels={labels}
          accountHref={accountHref}
          returnPath={returnPath}
        />
      </FirebaseAuthProvider>,
    );
  } catch (error) {
    console.error("[turku-favorite] mount failed", error);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void mount());
} else {
  void mount();
}
